/**
 * Page liste signalements — vue mairie (agent, manager, admin, super_admin).
 *
 * Tableau avec :
 *   - Filtres : statut, urgence, catégorie
 *   - Recherche texte (sujet, description, numéro)
 *   - Tri implicite : urgence > date
 *   - Pagination
 *
 * Refonte R6.1 (mai 2026) :
 *   - Topbar glass cohérent DashboardPage (LogoMark Landmark + label + Retour + ThemeToggle)
 *   - LogoMark Hammer accent tech au-dessus du titre + Geist tracking-r-snug
 *   - Card glass pour les filtres avec selects modernes
 *   - SignalementRow refait en card glass interactive avec hover lift + border accent
 *   - Pagination en pills glass
 *
 * Logique métier 100% préservée : filtres useEffect cancelled flag, fetch list paginé,
 * STATUT_BADGE_VARIANT/URGENCE_ICON/URGENCE_COLOR mappings, SelectField pattern.
 */

import { useEffect, useState, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Card, Alert, Input, LogoMark, SectionEyebrow } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import { SkeletonList } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import {
  ArrowLeft, Hammer, Landmark, Search, AlertTriangle, CircleAlert, Circle, ChevronRight,
  AlertCircle, Plus, Inbox, SearchX,
} from 'lucide-react';
import type {
  Signalement, SignalementStatut, SignalementUrgence, SignalementCategorie,
} from '@roronoa-collectivites/shared-types';
import { statutBadge } from '@/pages/service-technique/statut-badge';
import { BudgetSuiviCard } from '@/components/BudgetSuiviCard';
import {
  SIGNALEMENT_STATUTS, SIGNALEMENT_URGENCES, SIGNALEMENT_CATEGORIES,
} from '@roronoa-collectivites/shared-types';


const URGENCE_ICON: Record<SignalementUrgence, typeof AlertTriangle> = {
  urgent: AlertTriangle,
  rapide: CircleAlert,
  normal: Circle,
};

const URGENCE_COLOR: Record<SignalementUrgence, string> = {
  urgent: 'text-urgent',
  rapide: 'text-warning',
  normal: 'text-ink-subtle',
};

export function SignalementsListPage() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  // L'agent ne voit QUE les fiches qui lui sont assignées (la liste complète
  // reste réservée aux managers — décision L13.11). L'endpoint /mine existait
  // déjà côté serveur mais n'était utilisé par aucune page.
  const estAgent = user?.role === 'agent';

  const [items, setItems] = useState<Signalement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statut, setStatut] = useState<SignalementStatut | ''>('');
  const [urgence, setUrgence] = useState<SignalementUrgence | ''>('');
  const [categorie, setCategorie] = useState<SignalementCategorie | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const charger = estAgent
          ? api.serviceTechnique.signalements.listMine
          : api.serviceTechnique.signalements.list;
        const result = await charger({
          statut: statut || undefined,
          urgence: urgence || undefined,
          categorie: categorie || undefined,
          search: search || undefined,
          estEtatDesLieux: false,
          page,
          pageSize,
        });
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
      } catch (err) {
        if (err instanceof ApiException) setError(err.payload.error);
        else setError('Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statut, urgence, categorie, search, page, estAgent]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // L13.15 — distingue « liste vraiment vide » et « filtres sans résultat »
  // pour afficher l'EmptyState adapté (CTA création vs réinitialiser filtres).
  const hasActiveFilters = Boolean(statut || urgence || categorie || search.trim());

  return (
    <div className="min-h-screen text-ink">
      {/* ===== Topbar glass (identique aux autres pages service-technique) ===== */}
      <AppHeader module="Service technique" accent="tech" />

      {/* Sub-nav partagée (flex-wrap, menu déroulant Planning) */}
      <nav
        aria-label="Navigation Service technique"
        className="border-b border-border backdrop-blur-glass px-4 py-2"
        style={{ backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))' }}
      >
        <div className="mx-auto max-w-5xl">
          <ServiceTechniqueNav active="incidents" />
        </div>
      </nav>

      <main id="main-content" className="mx-auto max-w-5xl px-4 py-8 sm:px-6 animate-fade-in">
        {/* Vague 4d — suivi Budget en lecture seule (§5-5) : le staff VOIT le
            restant des enveloppes que ses devis consomment ; la Comptabilité règle. */}
        {!estAgent && <div className="mb-4"><BudgetSuiviCard /></div>}

        {/* En-tête de page : LogoMark Hammer + titre + bouton Nouveau */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <LogoMark icon={Hammer} accent="tech" size="lg" />
            <div className="min-w-0">
              <SectionEyebrow as="div">Service technique</SectionEyebrow>
              <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
                {estAgent ? 'Mes fiches' : 'Signalements'}
              </h1>
              <p className="mt-0.5 text-sm text-ink-muted">
                {estAgent
                  ? `${total} fiche${total !== 1 ? 's' : ''} qui vous ${total !== 1 ? 'sont' : 'est'} assignée${total !== 1 ? 's' : ''}`
                  : `${total} signalement${total !== 1 ? 's' : ''} au total`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={() => navigate('/service-technique/signaler')}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Nouveau</span>
            </Button>
          </div>
        </div>

        {/* Filtres en Card glass */}
        <Card variant="glass" padded>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-2 relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" aria-hidden="true" />
              <Input
                aria-label="Rechercher une fiche (sujet, description, numéro)"
                placeholder="Rechercher (sujet, description, numéro…)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <SelectField
              label="Statut"
              value={statut}
              onChange={(v) => {
                setStatut(v as SignalementStatut | '');
                setPage(1);
              }}
              options={SIGNALEMENT_STATUTS}
            />
            <SelectField
              label="Urgence"
              value={urgence}
              onChange={(v) => {
                setUrgence(v as SignalementUrgence | '');
                setPage(1);
              }}
              options={SIGNALEMENT_URGENCES}
            />
            <SelectField
              label="Catégorie"
              value={categorie}
              onChange={(v) => {
                setCategorie(v as SignalementCategorie | '');
                setPage(1);
              }}
              options={SIGNALEMENT_CATEGORIES}
            />
          </div>
        </Card>

        {/* Liste */}
        <div className="mt-4 space-y-2">
          {loading && <SkeletonList count={6} label="Chargement des signalements" />}

          {error && (
            <Alert variant="error" icon={AlertCircle}>
              {error}
            </Alert>
          )}

          {!loading && !error && items.length === 0 && (
            hasActiveFilters ? (
              <EmptyState
                icon={SearchX}
                tone="neutral"
                title="Aucun résultat"
                description="Aucun signalement ne correspond aux filtres sélectionnés. Essayez d'élargir votre recherche."
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setStatut('');
                      setUrgence('');
                      setCategorie('');
                      setSearch('');
                      setPage(1);
                    }}
                  >
                    Réinitialiser les filtres
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Inbox}
                tone="tech"
                title="Aucun signalement"
                description="Aucun signalement n'a encore été enregistré pour cette collectivité."
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/service-technique/signaler')}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Nouveau signalement
                  </Button>
                }
              />
            )
          )}

          {!loading && items.map((s) => (
            <SignalementRow
              key={s.id}
              s={s}
              onClick={() => navigate(`/service-technique/signalements/${s.id}`)}
            />
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Précédent
            </Button>
            <span className="text-sm text-ink-muted tabular-nums">
              Page {page} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Suivant
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// Composants internes
// ============================================================================

function SignalementRow({ s, onClick }: { s: Signalement; onClick: () => void }) {
  const UrgenceIcon = URGENCE_ICON[s.urgence];
  const badge = statutBadge(s);
  const categorieLabel = SIGNALEMENT_CATEGORIES.find((x) => x.value === s.categorie)?.label ?? s.categorie;

  // Tuile en card glass avec halo tech subtil + border tech-30% si urgent
  const isUrgent = s.urgence === 'urgent';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group block w-full text-left rounded-r-xl border backdrop-blur-glass px-4 py-3 shadow-soft',
        'transition-all duration-200 ease-out-expo',
        'hover:-translate-y-0.5 hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        isUrgent ? 'border-urgent/40' : 'border-border hover:border-tech/30',
      ].join(' ')}
      style={{
        backgroundImage: isUrgent
          ? 'radial-gradient(160px 80px at 0% 50%, oklch(var(--c-urgent) / 0.12), transparent 70%), linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))'
          : 'linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))',
      }}
    >
      <div className="flex items-center gap-3">
        <UrgenceIcon className={`h-5 w-5 shrink-0 ${URGENCE_COLOR[s.urgence]}`} aria-hidden="true" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-ink-muted">{s.numero}</span>
            <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
            {s.source === 'interne' && (
              <Badge variant="neutral" size="sm">Interne</Badge>
            )}
            <Badge variant="tech" size="sm">{categorieLabel}</Badge>
            {/* M12 — fiche en attente de sa saison d'intervention. */}
            {s.saisonOuverture && s.saisonOuverture > new Date().toISOString().slice(0, 10) && (
              <Badge variant="neutral" size="sm" title={s.saisonMotif ?? 'Intervention saisonnière'}>
                🍂 dès le {new Date(`${s.saisonOuverture}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' })}
              </Badge>
            )}
            {s.isAnonymous && (
              <Badge variant="neutral" size="sm" title="Signalement déposé via le formulaire public anonyme">
                Anonyme
              </Badge>
            )}
          </div>
          <h3 className="mt-1 truncate font-medium text-ink">{s.sujet}</h3>
          <p className="mt-0.5 text-xs text-ink-subtle">
            {new Date(s.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
            {s.localisationAdresse && <> · {s.localisationAdresse}</>}
            {s.createurNom && <> · par {s.createurNom}</>}
          </p>
        </div>

        <ChevronRight className="h-4 w-4 shrink-0 text-ink-dim transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </div>
    </button>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  // RGAA 11.1 (audit du 25/09, axe « select-name ») : l'étiquette était visible mais non reliée.
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium uppercase tracking-r-eyebrow text-ink-subtle">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
      >
        <option value="">Tous</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

